import countryCodes, {
  findCountryByCode,
  findCountryByDialCode,
} from "@/data/countryCodes";

/**
 * Parse a stored phone value into its parts.
 * Handles both new format (+66812345678) and legacy Thai format (0812345678).
 */
export function parsePhoneNumber(raw) {
  if (!raw) return { countryCode: "TH", dialCode: "+66", nationalNumber: "" };

  const cleaned = String(raw).trim();

  // New international format: starts with +
  if (cleaned.startsWith("+")) {
    // Longest match: try 4-digit, 3-digit, 2-digit, then 1-digit dial codes
    for (let len = 4; len >= 1; len--) {
      const candidate = "+" + cleaned.slice(1, 1 + len);
      const country = findCountryByDialCode(candidate);
      if (country) {
        return {
          countryCode: country.code,
          dialCode: country.dialCode,
          nationalNumber: cleaned.slice(1 + len),
        };
      }
    }
    // Unknown country code — return as-is with unknown marker
    return { countryCode: "TH", dialCode: "+66", nationalNumber: cleaned.slice(1) };
  }

  // Legacy Thai format: 10-digit starting with 0
  if (/^0\d{8,9}$/.test(cleaned)) {
    return {
      countryCode: "TH",
      dialCode: "+66",
      nationalNumber: cleaned.slice(1), // remove leading 0
    };
  }

  // Fallback: treat as Thai national number
  return { countryCode: "TH", dialCode: "+66", nationalNumber: cleaned };
}

/**
 * Combine dial code + local input into storage format.
 * Strips non-digits from localInput, removes leading 0 for Thai numbers.
 * Returns e.g. "+66812345678"
 */
export function formatPhoneForStorage(dialCode, localInput) {
  if (!localInput) return "";
  let digits = localInput.replace(/[^0-9]/g, "");
  if (!digits) return "";

  // Strip leading 0 for Thai numbers (local convention)
  if (dialCode === "+66" && digits.startsWith("0")) {
    digits = digits.slice(1);
  }

  return dialCode + digits;
}

/**
 * Format a stored phone value for display.
 * e.g. "+66812345678" → "+66 81-234-5678"
 * e.g. "0812345678" (legacy) → "+66 81-234-5678"
 */
export function formatPhoneForDisplay(storedValue) {
  if (!storedValue) return "";

  const { countryCode, dialCode, nationalNumber } = parsePhoneNumber(storedValue);
  if (!nationalNumber) return storedValue;

  const formatted = formatNationalNumber(nationalNumber, countryCode);
  return `${dialCode} ${formatted}`;
}

/**
 * Format digits as the user types for the local input field.
 * For Thai: XX-XXX-XXXX (with leading 0 restored)
 * For others: generic grouping
 */
export function formatLocalInput(rawInput, countryCode) {
  if (!rawInput) return "";
  const digits = rawInput.replace(/[^0-9]/g, "");
  if (!digits) return "";

  if (countryCode === "TH") {
    return formatThaiLocal(digits);
  }

  // Generic grouping: XXX-XXX-XXXX
  return digits.replace(
    /(\d{1,3})(\d{1,3})?(\d{1,4})?(\d+)?/,
    (_, a, b, c, d) => {
      let result = a;
      if (b) result += "-" + b;
      if (c) result += "-" + c;
      if (d) result += "-" + d;
      return result;
    }
  );
}

/**
 * Format Thai local number with leading 0 and dashes.
 * Input: "812345678" or "0812345678"
 * Output: "081-234-5678"
 */
function formatThaiLocal(digits) {
  let num = digits;
  // Add leading 0 if not present and looks like a mobile/landline
  if (!num.startsWith("0") && num.length <= 9) {
    num = "0" + num;
  }
  return num.replace(
    /(\d{1,3})(\d{1,3})?(\d{1,4})?/,
    (_, a, b, c) => {
      let result = a;
      if (b) result += "-" + b;
      if (c) result += "-" + c;
      return result;
    }
  );
}

/**
 * Format national number for display (after country code).
 * Country-specific formatting for known countries, generic for others.
 */
function formatNationalNumber(nationalNumber, countryCode) {
  const digits = nationalNumber.replace(/[^0-9]/g, "");
  if (!digits) return "";

  switch (countryCode) {
    case "TH": {
      // Thai: 81-234-5678
      return digits.replace(
        /(\d{2})(\d{3})(\d{4})/,
        "$1-$2-$3"
      );
    }
    case "US": {
      // US: 202-555-1234
      return digits.replace(
        /(\d{3})(\d{3})(\d{4})/,
        "$1-$2-$3"
      );
    }
    case "JP": {
      // Japan: 90-1234-5678
      return digits.replace(
        /(\d{2})(\d{4})(\d{4})/,
        "$1-$2-$3"
      );
    }
    case "SG":
    case "HK": {
      // Singapore/HK: 9123-4567
      return digits.replace(
        /(\d{4})(\d{4})/,
        "$1-$2"
      );
    }
    default: {
      // Generic: XXX-XXX-XXXX or similar
      return digits.replace(
        /(\d{1,3})(\d{1,3})?(\d{1,4})?(\d+)?/,
        (_, a, b, c, d) => {
          let result = a;
          if (b) result += "-" + b;
          if (c) result += "-" + c;
          if (d) result += "-" + d;
          return result;
        }
      );
    }
  }
}

/**
 * Get validation rules for a country's national number length.
 * Returns { min, max } for the national number (without country code).
 */
export function getValidationRules(countryCode) {
  const country = findCountryByCode(countryCode);
  if (!country || !country.phoneLength || country.phoneLength.length === 0) {
    return { min: 4, max: 15 };
  }
  return {
    min: Math.min(...country.phoneLength),
    max: Math.max(...country.phoneLength),
  };
}
